import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useInstalmentsList } from './hooks'
import { Instalment, InstalmentItem } from './types'
import { CCardBody } from '@coreui/react-pro'
import { Badge, InputRef, Table, TableColumnsType, Tag } from 'antd'
import { path } from '../../../../libs'
import { ColumnsType } from 'antd/lib/table'
import { FilterValue } from 'antd/es/table/interface'
import { getColumnSearchProps } from '../../tableFunctions'
import { useLocation } from 'react-router-dom'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

type LocationState = {
  status: string
  userId: string
}

export const InstalmentsList = () => {
  const {
    instalments,
    totalCountOfInstalments,
    loading,
    onChangeInstalmentsList,
  } = useInstalmentsList()
  const query = useQuery()

  const location = useLocation()
  const locationState = location?.state as LocationState

  useEffect(() => {
    const idParam = query.get('id')
    if (idParam) {
      setSearchText((prev) => ({ ...prev, id: idParam }))
    }
  }, [])

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>(locationState?.userId ? { userId: locationState.userId } : {})
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    status: locationState?.status ? [locationState.status] : null,
  })

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeInstalmentsList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  type InstallmentStatus = 'in_progress' | 'completed' | 'cancelled' | 'closed'

  const installmentStatusMap: Record<
    InstallmentStatus,
    { label: string; color: string }
  > = {
    in_progress: { label: 'In Progress', color: 'blue' },
    completed: { label: 'Completed', color: 'green' },
    cancelled: { label: 'Cancelled', color: 'red' },
    closed: { label: 'Closed', color: 'gray' },
  }
  const planTypeMap: Record<string, string> = {
    to_funded: '50/50',
    monthly_3: '4/25',
  }
  const columns: ColumnsType<Instalment> = [
    {
      title: 'Instalment ID',
      dataIndex: 'id',
      ...getColumnSearchProps<Instalment>(
        'id',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      ...getColumnSearchProps<Instalment>(
        'orderId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
    },
    {
      title: 'User ID',
      ...getColumnSearchProps<Instalment>(
        'userId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: Instalment) => (
        <div>
          {item.user?.isTest ? (
            <Badge count={'Test'} offset={[10, -10]}>
              <a
                href={path.clients.details(String(item.userId))}
                target="_blank"
              >
                {item.userId}
              </a>
            </Badge>
          ) : (
            <a href={path.clients.details(String(item.userId))} target="_blank">
              {item.userId}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Instalment type',
      dataIndex: 'planType',
      filterMultiple: false,
      filters: [
        {
          text: '50/50',
          value: 'to_funded',
        },
        {
          text: '4/25',
          value: 'monthly_3',
        },
      ],
      render: (planType: string) => planTypeMap[planType] || planType,
    },
    {
      title: 'Property ID',
      dataIndex: 'propertyId',
      ...getColumnSearchProps<Instalment>(
        'propertyId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      render: (item, record) => (
        <div>
          {record.propertyId} {record.propertyName}
        </div>
      ),
    },
    {
      title: 'Tokens amount',
      dataIndex: 'tokenAmount',
      render: (item) => Number(item),
    },
    {
      title: 'Total amount',
      dataIndex: 'totalAmount',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'Instalment status',
      dataIndex: 'status',
      filters: [
        {
          text: 'In Progress',
          value: 'in_progress',
        },
        {
          text: 'Completed',
          value: 'completed',
        },
        {
          text: 'Cancelled',
          value: 'cancelled',
        },
        {
          text: 'Closed',
          value: 'closed',
        },
      ],
      defaultFilteredValue: locationState?.status
        ? [locationState?.status]
        : null,
      render: (status: InstallmentStatus) => {
        const data = installmentStatusMap[status as InstallmentStatus]
        return <Tag color={data?.color}>{data?.label || status}</Tag>
      },
    },
    {
      title: 'Recalculation',
      dataIndex: 'recalculation',
      render: (recalc) => {
        if (!recalc || Object.keys(recalc).length === 0) {
          return '-'
        }

        return (
          <div>
            <div>
              <strong>Overdue days:</strong> {recalc.overdueDays}
            </div>
            <div>
              <strong>Penalty %:</strong> {+recalc.penaltyPercent} %
            </div>
            <div>
              <strong>Penalty amount:</strong> {+recalc.penaltyAmount} USD
            </div>
            <div>
              <strong>Amount $ left:</strong> {+recalc.leftAmount} USD
            </div>
            <div>
              <strong>Token left:</strong> {+recalc.leftToken}
            </div>
          </div>
        )
      },
    },
    // {
    //   title: 'Actions',
    //   fixed: 'right',
    //   width: 100,
    //   render: (item) => {
    //     return (
    //       <>
    //         <CButton
    //           color="danger"
    //           className="me-2"
    //           title="Cancel instalment"
    //           href={path.billing.deposit.edit(item.transactionId)}
    //         >
    //           <CIcon icon={cilDelete} />
    //         </CButton>
    //       </>
    //     )
    //   },
    // },
  ]

  const planStatusMap: Record<
    string,
    { label: string; color: 'success' | 'warning' | 'error' }
  > = {
    completed: {
      label: 'Completed',
      color: 'success',
    },
    planned: {
      label: 'Planned',
      color: 'warning',
    },
    cancelled: {
      label: 'Cancelled',
      color: 'error',
    },
  }

  const expandColumns: TableColumnsType<InstalmentItem> = [
    {
      title: 'Stage',
      dataIndex: 'sequenceNumber',
      sorter: (a, b) => a.sequenceNumber - b.sequenceNumber,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transactionRequestId',
      key: 'transactionRequestId',
      render: (transactionRequestId) => {
        if (transactionRequestId != 0) {
          return (
            <>
              <a
                href={path.billing.deposit.edit(transactionRequestId)}
                target="_blank"
              >
                {transactionRequestId}
              </a>
            </>
          )
        } else {
          return <>Nova wallet</>
        }
      },
    },
    { title: 'Planned date', dataIndex: 'plannedDate', key: 'plannedDate' },
    { title: 'Real date', dataIndex: 'paymentDate', key: 'paymentDate' },
    {
      title: 'Amount',
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      render: (item) => Number(item),
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (status: string) => {
        const data = planStatusMap[status]
        return <Tag color={data?.color}>{data?.label || status}</Tag>
      },
    },
  ]

  const expandedRowRender = (record: Instalment) => (
    <Table<InstalmentItem>
      columns={expandColumns}
      dataSource={record.installmentPlan || []}
      pagination={false}
      rowKey={(item) => `${record.id}-${item.plannedDate}`}
    />
  )
  return (
    <CCardBody>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
          }}
        ></div>
      </div>
      <Table
        dataSource={instalments}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="id"
        columns={columns}
        expandable={{ expandedRowRender }}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfInstalments,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters: { ...filtersState, ...filters },
          })
        }
      />
    </CCardBody>
  )
}
